import React from 'react';
import { ExternalLink, BadgeCheck } from 'lucide-react';

// Canonical Companies Office / NZBN verification block (D-057).
// Source of truth: /app/memory/founder_vault/_CORPORATE_STRUCTURE.md §8/§9.
// Render variants:
//   compact = true   → single-line footer-style strip
//   compact = false  → full card with table, suitable for Trust Centre, Security, Compliance.

const NZ_COMPANY_NUMBER = '6860633';
const NZ_NZBN = '9429046803306';
const NZ_GST = '125-955-886';
const CO_URL = `https://app.companiesoffice.govt.nz/companies/app/ui/pages/companies/${NZ_COMPANY_NUMBER}`;
const NZBN_URL = `https://www.nzbn.govt.nz/mynzbn/nzbndetails/${NZ_NZBN}/`;

export const CompaniesOfficeTrustStrip = ({ className = '' }) => (
  <div
    data-testid="companies-office-trust-strip"
    className={`inline-flex max-w-full min-w-0 flex-col gap-2 border border-neutral-900 bg-white px-4 py-3 ${className}`}
  >
    <div className="flex flex-wrap items-center gap-2">
      <BadgeCheck size={14} strokeWidth={1.7} className="flex-shrink-0" />
      <span className="label-overline tracking-wider">Independently Verifiable</span>
    </div>
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-700 font-mono">
      <a
        data-testid="hero-trust-companies-office"
        href={CO_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 underline hover:no-underline"
      >
        NZ Company No. <strong className="text-neutral-900">{NZ_COMPANY_NUMBER}</strong>
        <ExternalLink size={11} strokeWidth={1.5} />
      </a>
      <span className="text-neutral-300">·</span>
      <a
        data-testid="hero-trust-nzbn"
        href={NZBN_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 underline hover:no-underline"
      >
        NZBN <strong className="text-neutral-900">{NZ_NZBN}</strong>
        <ExternalLink size={11} strokeWidth={1.5} />
      </a>
      <span className="text-neutral-300">·</span>
      <span>NZ GST <strong className="text-neutral-900">{NZ_GST}</strong></span>
    </div>
  </div>
);

export const CompaniesOfficeStrip = () => (
  <div
    data-testid="companies-office-strip"
    className="border-t border-neutral-200 bg-white"
  >
    <div className="mx-auto flex max-w-[1440px] flex-col gap-2 px-5 py-3 text-xs text-neutral-600 md:flex-row md:items-center md:justify-between md:px-8 font-mono">
      <span>
        © Whaitiri Black Limited · trading as GIWAHS. Registered in New Zealand.
      </span>
      <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span>NZ Company № <strong className="text-neutral-900">{NZ_COMPANY_NUMBER}</strong></span>
        <span className="text-neutral-300">·</span>
        <span>NZBN <strong className="text-neutral-900">{NZ_NZBN}</strong></span>
        <span className="text-neutral-300">·</span>
        <span>NZ GST <strong className="text-neutral-900">{NZ_GST}</strong></span>
        <span className="text-neutral-300">·</span>
        <a
          data-testid="companies-office-link"
          href={CO_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 underline hover:no-underline"
        >
          Companies Office <ExternalLink size={11} strokeWidth={1.5} />
        </a>
        <a
          data-testid="nzbn-register-link"
          href={NZBN_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 underline hover:no-underline"
        >
          NZBN Register <ExternalLink size={11} strokeWidth={1.5} />
        </a>
      </span>
    </div>
  </div>
);

export const CompaniesOfficeBlock = ({ overline = '/ Companies Office verification' }) => (
  <section
    data-testid="companies-office-block"
    className="border-b border-black bg-white"
  >
    <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-16">
      <span className="label-overline">{overline}</span>
      <h2 className="mt-3 font-display text-2xl tracking-tighter md:text-3xl" style={{ fontWeight: 600 }}>
        Verify us independently.
      </h2>
      <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-700">
        <strong>Whaitiri Black Limited (trading as GIWAHS)</strong> is a registered New Zealand
        company. Company details may be independently verified via the New Zealand Companies
        Office and the NZBN Register.
      </p>
      <div className="mt-8 overflow-x-auto">
        <table className="w-full min-w-[520px] border-collapse border border-black bg-white text-sm">
          <thead className="bg-neutral-100">
            <tr>
              <th className="border border-black p-4 text-left label-overline">Identifier</th>
              <th className="border border-black p-4 text-left label-overline">Value</th>
              <th className="border border-black p-4 text-left label-overline">Verify on</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-black p-4 font-medium">NZ Companies Office number</td>
              <td className="border border-black p-4 font-mono">{NZ_COMPANY_NUMBER}</td>
              <td className="border border-black p-4">
                <a
                  data-testid="companies-office-row-link"
                  href={CO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 underline hover:no-underline"
                >
                  <BadgeCheck size={14} strokeWidth={1.5} /> Companies Office Aotearoa NZ
                  <ExternalLink size={12} strokeWidth={1.5} />
                </a>
              </td>
            </tr>
            <tr>
              <td className="border border-black p-4 font-medium">NZBN</td>
              <td className="border border-black p-4 font-mono">{NZ_NZBN}</td>
              <td className="border border-black p-4">
                <a
                  data-testid="nzbn-row-link"
                  href={NZBN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 underline hover:no-underline"
                >
                  <BadgeCheck size={14} strokeWidth={1.5} /> NZBN Register
                  <ExternalLink size={12} strokeWidth={1.5} />
                </a>
              </td>
            </tr>
            <tr>
              <td className="border border-black p-4 font-medium">NZ GST number</td>
              <td className="border border-black p-4 font-mono">{NZ_GST}</td>
              <td className="border border-black p-4 text-neutral-700">
                <span className="inline-flex items-center gap-1.5">
                  <BadgeCheck size={14} strokeWidth={1.5} /> Whaitiri Black Limited is NZ
                  GST-registered. Tax invoices via Stripe Tax.
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-xs text-neutral-500 font-mono">
        NZ GST № {NZ_GST}. Director: Jay Elkington (100% shareholder). Company registration
        details are available via the NZ Companies Office register linked above.
      </p>
    </div>
  </section>
);

export default CompaniesOfficeBlock;
